import { useState } from "react";
import { motion } from "framer-motion";
import {
  FileBarChart,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Clock,
  Download,
} from "lucide-react";
import { Badge, springs } from "@/components/ui";
import { useTranslation } from "@audit-app/utils";
import type { ReportView } from "./ReportListView";

const STATUS_CONFIG: Record<
  string,
  { dotColor: string; icon: React.ReactNode }
> = {
  ready: {
    dotColor: "#4ade80",
    icon: <CheckCircle2 size={12} className="text-green-400" />,
  },
  generating: {
    dotColor: "var(--color-accent)",
    icon: <Loader2 size={12} className="animate-spin text-accent" />,
  },
  failed: {
    dotColor: "#f87171",
    icon: <AlertCircle size={12} className="text-red-400" />,
  },
};

interface ReportGridCardProps {
  report: ReportView;
  index: number;
  onClick: (id: string) => void;
  onDownload: (report: ReportView) => void;
}

export function ReportGridCard({
  report,
  index,
  onClick,
  onDownload,
}: ReportGridCardProps) {
  const { t } = useTranslation(["reports"]);
  const [isHovered, setIsHovered] = useState(false);
  const config = STATUS_CONFIG[report.status] ?? STATUS_CONFIG.ready;
  const generatedDate = new Date(report.generatedAt).toLocaleDateString();

  return (
    <motion.div
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      transition={{
        ...springs.smooth,
        delay: Math.min(index, 16) * 0.03,
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onClick(report.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(report.id);
        }
      }}
      className="group relative flex flex-col rounded-card bg-card text-left cursor-pointer overflow-clip border border-transparent hover:border-border/60"
    >
      {/* Accent left bar */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent rounded-l-card"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isHovered ? 1 : 0 }}
        transition={springs.snappy}
        style={{ originY: 0.5 }}
      />

      {/* Hover glow */}
      <motion.div
        className="absolute inset-0 rounded-card pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        style={{
          boxShadow:
            "0 4px 24px -4px rgba(232, 134, 42, 0.1), 0 2px 8px -2px rgba(0,0,0,0.06)",
        }}
      />

      <div className="p-card flex flex-col flex-1">
        {/* Status + draft badge */}
        <div className="flex items-center gap-1.5 mb-3">
          <Badge
            color={{
              bg:
                report.status === "ready"
                  ? "rgba(74,222,128,0.15)"
                  : report.status === "failed"
                    ? "rgba(248,113,113,0.15)"
                    : "color-mix(in oklch, var(--color-accent) 15%, transparent)",
              text: config.dotColor,
            }}
          >
            <span className="inline-flex items-center gap-1">
              {config.icon}
              {t(`reports:status.${report.status}` as "reports:status.ready")}
            </span>
          </Badge>

          {report.isDraft && (
            <Badge color={{ bg: "rgba(161,161,166,0.15)", text: "#a1a1a6" }}>
              {t("reports:card.draft")}
            </Badge>
          )}
        </div>

        {/* Icon + audit name */}
        <div className="flex items-center gap-3 mb-1.5">
          <motion.div
            className="shrink-0 p-2 rounded-lg bg-accent/10"
            animate={{ rotate: isHovered ? [0, -6, 6, 0] : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <FileBarChart size={16} className="text-accent" />
          </motion.div>
          <div className="flex-1 min-w-0">
            {report.auditCode && (
              <span className="text-[11px] text-muted font-mono truncate block max-w-36">
                {report.auditCode}
              </span>
            )}
            <p className="text-sm font-semibold text-primary truncate">
              {report.auditName}
            </p>
          </div>
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-2 text-[11px] text-muted mb-3">
          <span className="flex items-center gap-1">
            <Clock size={10} className="shrink-0" />
            {generatedDate}
          </span>
          {report.version && (
            <>
              <span className="text-border">|</span>
              <span className="font-medium text-secondary">
                v{report.version}
              </span>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-border mt-auto mb-2.5 pt-2" />

        {/* Footer: download button */}
        <div className="flex items-center justify-end">
          {report.status === "ready" && report.pdfUrl && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(report);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-accent border border-accent/30 hover:bg-accent/10 transition-colors"
            >
              <Download size={13} />
              {t("reports:list.rowMenu.download")}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
